/* eslint-disable max-len */
import * as React from 'react';
import { StylesProps } from 'theme/jss-types';
import { HocOptions } from 'components/common/generic-hoc.types';
import GenericHoc from 'components/common/generic-hoc';
import { rateCalculatorStyles } from './rate-calculator-new.styles';
import {
    Button,
    Collapse,
    Empty,
    Form,
    Row,
    Col,
    Space,
    message,
} from 'antd';
import {
    BulbOutlined,
    CheckCircleFilled,
    HistoryOutlined,
    MinusCircleOutlined,
    PlusCircleOutlined,
    RightOutlined,
    WarningFilled,
} from '@ant-design/icons';
import { ErrorList, Service } from 'library/Types';
import { useTranslation } from 'react-i18next';
import { getFormFieldFromMapping } from 'components/create-consignment/single-cn-creation/components/generic-field-renderer';
import {
    fetchRateCalculatorFormConfig,
    getServiceabilityRateTat,
    getServiceabilityRateTatWithPx,
} from 'network/common.api';
import Loader from 'components/common/Loader';
import { ReduxStore } from 'reducers/redux.types';
import { CustomerPortalConfig } from 'types/master-data-types';
import { convertToAPIBodySaveSingle, getPiecesDetail, getChildClientFallback } from 'components/create-consignment/single-cn-creation/single-consignment-helper';
import { transformPayloadNew } from 'components/create-consignment/single-cn-creation/single-cn-new-px';
import SimpleAddressField from './simple-address-field';
import { RateCalcSrcAddress, RateCalcDstAddress, DEFAULT_RATE_CALC_FORM_CONFIG } from './rate-calculator.constants';
import parcelSvg from '../../assets/delivery-parcel.svg';

const { Panel } = Collapse;
const { useState, useEffect, useCallback } = React;

interface FormConfig {
    is_enabled: boolean;
    form_layout: string[];
    sub_layout: Record<string, string[]>;
}

interface PanelRefsMap {
    [key: string]: React.RefObject<HTMLDivElement>;
}

interface Props extends StylesProps<ReturnType<typeof rateCalculatorStyles>> {
    softdataCreationUnifiedFlow: boolean;
    showRiskSurchargeType: boolean;
    customFieldsList: any[];
    allowedConsignmentFormType: any[];
    autofillAddressFieldsBasedOnAddressHierarchy: boolean;
}

const RateCalculatorNew = (props: Props) => {
    const {
        classes,
        softdataCreationUnifiedFlow,
        showRiskSurchargeType,
        customFieldsList,
        allowedConsignmentFormType,
        autofillAddressFieldsBasedOnAddressHierarchy,
    } = props;
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
    const [configLoading, setConfigLoading] = useState(true);
    const [services, setServices] = useState<Service[]>([]);
    const [fetching, setFetching] = useState(false);
    const [activeKeys, setActiveKeys] = useState<string[]>(['1']);
    const [activeUAVKeys, setActiveUAVKeys] = useState<string[]>([]);
    const [activeAVKeys, setActiveAVKeys] = useState<string[]>([]);
    const [panelRefs, setPanelRefs] = useState<PanelRefsMap>({});
    const [showTopShadow, setShowTopShadow] = useState(false);
    const [showBottomShadow, setShowBottomShadow] = useState(false);

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const availablePanelRef = React.useRef<HTMLDivElement>(null);
    const unavailablePanelRef = React.useRef<HTMLDivElement>(null);

    const params = {
        getValidationRules: () => [],
        isRequired: () => false,
        getIsRequiredValidation: () => false,
        isCurrentTab: true,
        loadTypeList: [],
        manufacturingCountryList: [],
        incotermsList: [],
        subLayout: formConfig?.sub_layout || {},
    };

    useEffect(() => {
        const load = async () => {
            setConfigLoading(true);
            const response = await fetchRateCalculatorFormConfig();
            if (response?.isSuccess && response?.data?.form_config) {
                setFormConfig(response.data.form_config);
            } else {
                setFormConfig(DEFAULT_RATE_CALC_FORM_CONFIG);
            }
            setConfigLoading(false);
        };
        load();
    }, []);

    useEffect(() => {
        if (services.length > 0) {
            const refs: PanelRefsMap = {};
            services.forEach((item: Service) => {
                refs[item.serviceTypeId] = React.createRef<HTMLDivElement>();
            });
            setPanelRefs(refs);
        }
    }, [services]);

    const checkScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (container) {
            setShowBottomShadow(
                container.scrollHeight > container.clientHeight
                    && container.scrollTop + container.clientHeight < container.scrollHeight,
            );
            setShowTopShadow(container.scrollTop > 0);
        }
    }, []);

    useEffect(() => {
        if (services.length > 0) {
            setTimeout(() => {
                checkScroll();
            }, 100);
        }
    }, [services, checkScroll]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            return () => {
                container.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
            };
        }
        return undefined;
    }, [checkScroll]);

    const loadServices = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }
        setFetching(true);
        const { childClient: _ignored, ...formData } = form.getFieldsValue();
        let response: any;

        if (softdataCreationUnifiedFlow) {
            formData.piecesDetail = getPiecesDetail(formData, [], []);
            response = await getServiceabilityRateTatWithPx(transformPayloadNew({
                ...formData,
                ...getChildClientFallback(),
            }, {
                showRiskSurchargeType,
                isServiceableForm: true,
                isCustomFieldAllowed: true,
                customFieldsList,
                source: 'CUSTOMER_PORTAL_SINGLE_V2',
            }));
        } else {
            response = await getServiceabilityRateTat(convertToAPIBodySaveSingle({
                ...formData,
                ...getChildClientFallback(),
            }, {
                showRiskSurchargeType,
                sendCustomFieldObject: true,
                customFieldsList,
                source: 'CUSTOMER_PORTAL_SINGLE_V2',
            }));
        }
        setServices(response?.data || []);
        if (!response?.isSuccess) {
            message.error(response?.errorMessage || t('failed_to_fetch_services'));
            setFetching(false);
            return;
        }
        const servicesData: Service[] = response?.data || [];
        const hasAvailable = servicesData.some((s: Service) => s?.serviceability_info?.is_serviceable);
        setActiveKeys(hasAvailable ? ['1'] : ['1', '2']);
        setFetching(false);
    };

    const handleAlternateOptionsToggle = (key: string, type: string) => {
        if (type === 'UAV') {
            const newActiveKeys = activeUAVKeys.includes(key)
                ? activeUAVKeys.filter((k) => k !== key)
                : [...activeUAVKeys, key];
            setActiveUAVKeys(newActiveKeys);
            if (!activeUAVKeys.includes(key)) {
                setTimeout(() => {
                    if (panelRefs[key] && panelRefs[key].current) {
                        panelRefs[key].current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }, 300);
            }
        } else {
            const newActiveKeys = activeAVKeys.includes(key)
                ? activeAVKeys.filter((k) => k !== key)
                : [...activeAVKeys, key];
            setActiveAVKeys(newActiveKeys);
            if (!activeAVKeys.includes(key)) {
                setTimeout(() => {
                    if (panelRefs[key] && panelRefs[key].current) {
                        panelRefs[key].current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }, 300);
            }
        }
    };

    const expandAllUnavailableServices = () => {
        const allUAVKeys = services?.filter(
            (item) => (!item?.serviceability_info?.is_serviceable || !(item as any).is_serviceable),
        ).map((item) => item.serviceTypeId);
        setActiveUAVKeys(allUAVKeys);
    };

    const collapseAllUnavailableServices = () => {
        setActiveUAVKeys([]);
    };

    const getRateCalcField = (fieldKey: string, formInstance: any, fieldParams: any) => {
        if (fieldKey === 'srcAddress') {
            return (
                <SimpleAddressField
                    field={RateCalcSrcAddress}
                    form={formInstance}
                    params={fieldParams}
                    autofillAddressFieldsBasedOnAddressHierarchy={autofillAddressFieldsBasedOnAddressHierarchy}
                />
            );
        }
        if (fieldKey === 'dstAddress') {
            return (
                <SimpleAddressField
                    field={RateCalcDstAddress}
                    form={formInstance}
                    params={fieldParams}
                    autofillAddressFieldsBasedOnAddressHierarchy={autofillAddressFieldsBasedOnAddressHierarchy}
                />
            );
        }
        return getFormFieldFromMapping(fieldKey, formInstance, fieldParams, false);
    };

    const renderTat = (item: Service) => {
        const showTat = allowedConsignmentFormType.includes('TAT');
        const tat = item?.period || item?.tat || (item as any)?.TAT;
        if (!tat || !showTat) {
            return <div className={classes.tatNew} />;
        }
        return (
            <div className={classes.columnNew}>
                <img alt="parcel" className={classes.tatIcon} src={parcelSvg} />
                <div className={classes.tatNew}>
                    {tat.toString().endsWith('Days') ? tat : `${tat} ${t('days')}`}
                </div>
            </div>
        );
    };

    const renderDeliveryTime = (item: Service) => {
        const showDeliveryTime = allowedConsignmentFormType.includes('Delivery Time');
        if (!item.deliveryTime || !showDeliveryTime) {
            return (
                <div className={classes.deliveryTimeNewNA}>
                    {t('not_available')}
                </div>
            );
        }
        return (
            <div className={classes.columnNew}>
                <HistoryOutlined />
                <div className={classes.deliveryTimeNew}>
                    {`${t('delivery_by')} ${item?.deliveryTime}`}
                </div>
            </div>
        );
    };

    const renderRate = (item: Service) => {
        if (!item.rate?.amount) return null;
        return (
            <div className={classes.finalPriceNew}>
                {item.rate?.currencySymbol ? `${item.rate.currencySymbol} ` : ''}
                {`${item.rate?.amount}`}
            </div>
        );
    };

    const renderAlternateOptions = (item: Service, type: string) => {
        if ((item.error_list?.length ?? 0) === 0) return null;
        const isExpanded = activeUAVKeys.includes(item.serviceTypeId) || activeAVKeys.includes(item.serviceTypeId);
        return (
            <div
                className={classes.alternateOptions}
                onClick={(e) => {
                    e.stopPropagation();
                    handleAlternateOptionsToggle(item.serviceTypeId, type);
                }}
            >
                {type === 'AV' ? t('alternate_options') : t('unavailable')}
                <div
                    className={classes.arrowIcon}
                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                >
                    <RightOutlined />
                </div>
            </div>
        );
    };

    const renderErrorList = (item: Service, type: string) => {
        if (item.error_list?.length === 0) {
            return null;
        }
        return (
            <div className={classes.errorList}>
                <div className={classes.errorHeading}>
                    <BulbOutlined />
                    {type === 'AV' ? `${t('see_why_other_options_for_this_service_are_not_available')} :` : `${t('see_why_unavailable')} :`}
                </div>
                <ul>
                    {item.error_list?.map((itemError: ErrorList) => (
                        <li key={itemError.error_code}>{itemError.error_message}</li>
                    ))}
                </ul>
            </div>
        );
    };

    // Available service card: [ServiceInfo: name + TAT + delivery] [RightCol: price + alt-options toggle]
    // WarningPanel (error list) rendered below row, state-controlled
    const renderAvailableService = (item: Service) => {
        const showErrorList = (item.error_list ?? []).length > 0;
        const isWarningExpanded = activeAVKeys.includes(item.serviceTypeId);
        return (
            <Row
                key={item.serviceTypeId}
                className={classes.serviceOption}
                ref={panelRefs[item.serviceTypeId]}
            >
                <Row className={classes.serviceCard} justify="space-between" align="middle">
                    <div>
                        <div className={classes.serviceItem}>
                            {item.serviceType}
                        </div>
                        <div className={classes.serviceInfoRow}>
                            {renderTat(item)}
                            {renderDeliveryTime(item)}
                        </div>
                    </div>
                    <div className={classes.serviceCardRight}>
                        {renderRate(item)}
                        {showErrorList && renderAlternateOptions(item, 'AV')}
                    </div>
                </Row>
                {showErrorList && (
                    <div
                        className={classes.errorListCollapse}
                        style={{ maxHeight: isWarningExpanded ? '400px' : '0' }}
                    >
                        {renderErrorList(item, 'AV')}
                    </div>
                )}
            </Row>
        );
    };

    // Unavailable service card: [ServiceInfo: muted name] [RightCol: "Unavailable" toggle]
    // WarningPanel (error list) rendered below row, state-controlled
    const renderUnavailableService = (item: Service) => {
        const showErrorList = (item.error_list ?? []).length > 0;
        const isWarningExpanded = activeUAVKeys.includes(item.serviceTypeId);
        return (
            <Row
                key={item.serviceTypeId}
                className={`${classes.serviceOption} ${classes.unavailableCard}`}
                ref={panelRefs[item.serviceTypeId]}
            >
                <div className={classes.unavailableCardHeader}>
                    <div className={classes.unavailableServiceName}>
                        {item.serviceType}
                    </div>
                    {showErrorList && renderAlternateOptions(item, 'UAV')}
                </div>
                {showErrorList && (
                    <div
                        className={classes.errorListCollapse}
                        style={{ maxHeight: isWarningExpanded ? '400px' : '0' }}
                    >
                        {renderErrorList(item, 'UAV')}
                    </div>
                )}
            </Row>
        );
    };

    const renderAvailableServicesHeader = () => {
        return (
            <div className={classes.servicesHeader}>
                <CheckCircleFilled style={{ color: '#27B479' }} />
                {t('available_services')}
            </div>
        );
    };

    const renderUnavailableServicesHeader = () => {
        return (
            <div className={classes.servicesHeader}>
                <WarningFilled style={{ color: 'red' }} />
                {t('unavailable_services')}
            </div>
        );
    };

    const renderExpandAll = () => {
        return (
            <Button
                className={classes.expandAllHeader}
                onClick={expandAllUnavailableServices}
                type="text"
                disabled={activeUAVKeys?.length === services?.filter(
                    (item) => (!item?.serviceability_info?.is_serviceable || !(item as any).is_serviceable),
                ).length}
            >
                <PlusCircleOutlined />
                {t('expand_all')}
            </Button>
        );
    };

    const renderCollapseAll = () => {
        return (
            <Button
                className={classes.expandAllHeader}
                onClick={collapseAllUnavailableServices}
                type="text"
                disabled={activeUAVKeys.length === 0}
            >
                <MinusCircleOutlined />
                {t('collapse_all')}
            </Button>
        );
    };

    const renderAllOptions = () => {
        return (
            <div className={classes.expandCollapseOptions}>
                {renderCollapseAll()}
                {renderExpandAll()}
            </div>
        );
    };

    const renderAvailableServices = (availableServices: Service[]) => {
        return (
            <div className={classes.servicesRelativeContainer}>
                <div
                    className={`${classes.shadowBase} ${classes.shadowTop}`}
                    style={{ opacity: showTopShadow ? 1 : 0 }}
                />
                <div
                    ref={scrollContainerRef}
                    className={classes.scrollContainerAvailable}
                    onScroll={checkScroll}
                >
                    {availableServices.map(renderAvailableService)}
                </div>
                <div
                    className={`${classes.shadowBase} ${classes.shadowBottom}`}
                    style={{ opacity: showBottomShadow ? 1 : 0 }}
                />
            </div>
        );
    };

    const renderUnavailableServices = (unavailableServices: Service[]) => {
        return (
            <Row className={classes.scrollContainerUnavailable}>
                {unavailableServices.map(renderUnavailableService)}
            </Row>
        );
    };

    const renderServices = () => {
        if (!services.length) return null;

        const availableServices = services.filter(
            (s) => s?.serviceability_info?.is_serviceable || (s as any)?.is_serviceable,
        );
        const unavailableServices = services.filter(
            (s) => !s?.serviceability_info?.is_serviceable && !(s as any)?.is_serviceable,
        );

        const handlePanelChange = (keys: string[]) => {
            setActiveKeys(keys);
            if (keys.includes('2') && !activeKeys.includes('2')) {
                setTimeout(() => {
                    if (unavailablePanelRef.current) {
                        unavailablePanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 300);
            }
            if (keys.includes('1') && !activeKeys.includes('1')) {
                setTimeout(() => {
                    if (availablePanelRef.current) {
                        availablePanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 300);
            }
        };

        return (
            <div className={classes.servicesMain}>
                <Collapse
                    activeKey={activeKeys}
                    onChange={handlePanelChange}
                    bordered={false}
                    expandIconPosition="end"
                >
                    <Panel
                        header={renderAvailableServicesHeader()}
                        key="1"
                        className={classes.panel}
                    >
                        <div ref={availablePanelRef}>
                            {availableServices.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {renderAvailableServices(availableServices)}
                                </Space>
                            )}
                        </div>
                    </Panel>
                    <Panel
                        header={renderUnavailableServicesHeader()}
                        key="2"
                        className={classes.panel}
                    >
                        <div ref={unavailablePanelRef}>
                            {unavailableServices.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {renderAllOptions()}
                                    {renderUnavailableServices(unavailableServices)}
                                </Space>
                            )}
                        </div>
                    </Panel>
                </Collapse>
            </div>
        );
    };

    if (configLoading) {
        return <Loader zIndex={10} />;
    }

    if (!formConfig?.is_enabled) {
        return null;
    }

    return (
        <div className={classes.main}>
            <h2 className={classes.headingTitle}>{t('rate_calculator')}</h2>
            <Row gutter={10}>
                <Col span={12}>
                    <div className={classes.colData}>
                        <Form form={form} layout="vertical">
                            <Row gutter={10}>
                                {formConfig.form_layout.map((fieldKey: string) => (
                                    getRateCalcField(fieldKey, form, params)
                                ))}
                            </Row>
                        </Form>
                        <Button
                            type="primary"
                            loading={fetching}
                            onClick={loadServices}
                            className={classes.calculateButton}
                        >
                            {t('calculate_rate')}
                        </Button>
                    </div>
                </Col>
                <Col span={12}>
                    {services.length > 0 && (
                        <div className={classes.colData}>
                            {renderServices()}
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const customerPortalConfig: CustomerPortalConfig = state?.master?.config?.customer_portal_config;
    const customFieldsList = state?.master?.config?.custom_fields_list_for_form || [];
    const softdataCreationUnifiedFlow = state?.master?.parts_to_show?.softdata_creation_unified_flow_enabled;
    const allowedConsignmentFormType = state?.master?.config?.customer_portal_ops_config
        ?.consignment_flow_type_config?.config_value?.allowed_consignment_form_type_customer_portal || [];
    return {
        softdataCreationUnifiedFlow,
        customFieldsList,
        showRiskSurchargeType: customerPortalConfig?.show_risk_surcharge_type,
        allowedConsignmentFormType,
        autofillAddressFieldsBasedOnAddressHierarchy: state?.master
            ?.parts_to_show?.autofill_address_fields_based_on_address_hierarchy,
    };
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: rateCalculatorStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(RateCalculatorNew);
